import * as React from "react";
import { SpirometryResults, StudyLungVolumeRelevantParameters } from './Shared/PFTAPITypes';
import { LocalizedDateTime, DateTimeAccuracy } from '../misc/components/LocalizedDateTime';
import { PFTResultParameters } from './Shared/PFTResultsParameters';
import { Grades, TestAcceptabilityTable } from './Shared/TestAcceptabilityTable';
import { FixedCommentsSelectors } from './Shared/FixedCommentsSelectors';
import { FixedCommentsState, ToggleSessionFixedCommentsVisibilityAction, ToggleTestFixedCommentsVisibilityAction, PageValidatedAction, LoadAnalysisSetOverreadAction } from "./Shared/UseFixedComments";
import { SelectedTestCommentsCount, SelectedSessionCommentsCount } from "./Shared/Selectors/FixedCommentSelectors";
import { CommentModals } from "./Shared/CommentsModals";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from './Shared/UseAcceptability';
import RepeatabilityTable from "./Shared/RepeatabilityTable";
import { useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

export interface LungVolumeResultsTableProps {
    isViewOnly: boolean;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    resultData: SpirometryResults;
    fixedCommentsState: FixedCommentsState;
    fixedCommentsDispatch;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
    grades: Grades;
    studyLungVolumeRelevantParameters?: StudyLungVolumeRelevantParameters;
}

export const LungVolumeResultsTable = ({ isViewOnly, isRejected, isSiteAnalysisSet, resultData, studyLungVolumeRelevantParameters, fixedCommentsState, fixedCommentsDispatch, shouldValidateFixedComments, acceptabilityState, acceptabilityDispatch, grades }: LungVolumeResultsTableProps) => {
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchOverreading = async () => {
            const analysisOverreading = await legacyClient.getLungVolumeAnalysisSetOverreading(resultData.AnalysisSetId);

            fixedCommentsDispatch(LoadAnalysisSetOverreadAction.create(analysisOverreading));
        }

        fetchOverreading();
    }, [resultData.AnalysisSetId, fixedCommentsDispatch])

    
    return (
        <>
            <table id="LVResultsTable" className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {
                            resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                            )}
                        <th>Best</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Time</td>
                        {
                            resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <td key={`TestDateTime${t.TestId}`} data-automationid={`test-occurred-${t.TestNumber}`}>
                                    <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                </td>
                            )}
                        <td></td>
                    </tr>

                    <PFTResultParameters
                        isLoading={acceptabilityState.loadingBestValues}
                        results={resultData}
                        isRejected={isRejected}
                        studyRelevantParameters={studyLungVolumeRelevantParameters}
                        showConfiguredParametersOnly={true}
                    />

                    <RepeatabilityTable
                        isLoading={acceptabilityState.loadingBestValues}
                        results={resultData}
                        isRejected={isRejected}
                        studyRelevantParameters={studyLungVolumeRelevantParameters}
                        showConfiguredParametersOnly={true}
                    />

                    <TestAcceptabilityTable
                        isViewOnly={isViewOnly}
                        isLoading={acceptabilityState.loadingBestValues}
                        testResults={acceptabilityState.spirometryResults.TestResults}
                        grades={grades}
                        displayTechnicianAcceptability={!isSiteAnalysisSet}
                        onAcceptabilityChange={(id, checked) =>
                            acceptabilityDispatch(
                                ToggleTestAcceptabilityAction.create(id, checked)
                            )
                        }
                    />

                    <FixedCommentsSelectors
                        testResults={resultData.TestResults}
                        selectedTestCommentsCount={SelectedTestCommentsCount(fixedCommentsState.testFixedComments)}
                        selectedSessionCommentsCount={
                            SelectedSessionCommentsCount(fixedCommentsState.sessionFixedComments)
                        }
                        onTestFixedCommentClick={testId =>
                            fixedCommentsDispatch(
                                ToggleTestFixedCommentsVisibilityAction.create(testId)
                            )
                        }
                        onSessionFixedCommentClick={() =>
                            fixedCommentsDispatch(
                                ToggleSessionFixedCommentsVisibilityAction.create()
                            )
                        }
                        addCommentsSuffix={true}
                    />

                </tbody>
            </table>
            <CommentModals
                editable={!isViewOnly}
                sessionFixedComments={fixedCommentsState.sessionFixedComments}
                testFixedComments={fixedCommentsState.testFixedComments}
                setIsPageValidated={(isValidated) => fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                isPageValid={fixedCommentsState.isPageValidated}
                dispatch={fixedCommentsDispatch}
            />
        </>
    );
}
